import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { EventPresentationData, PresentationTeam, PresentationSubmission } from '../utils/types';
import { apiGet, ApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const EventPresentationView: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [presentationData, setPresentationData] = useState<EventPresentationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [downloading, setDownloading] = useState(false);
  const { token, user } = useAuth();

  // Check if current user has organizer role (case-insensitive)
  const isOrganizer = user?.roles?.some(role => role.toUpperCase() === 'ORGANIZER') || false;

  useEffect(() => {
    if (eventId && token && isOrganizer) {
      loadPresentationData();
    } else if (!isOrganizer) {
      setError('Only organizers can access presentation data');
      setLoading(false);
    }
  }, [eventId, token, isOrganizer]);

  const loadPresentationData = async () => {
    if (!token || !eventId) return;
    
    setLoading(true);
    setError('');
    
    try {
      const data = await apiGet<EventPresentationData>(`/presentation/events/${eventId}/teams`, token);
      setPresentationData(data);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Failed to load presentation data');
      console.error('Failed to load presentation data:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadPresentationJSON = async () => {
    if (!presentationData) return;
    
    setDownloading(true);
    try {
      // Create a comprehensive JSON file for OBS/Companion integration
      const exportData = {
        ...presentationData,
        export_info: {
          exported_at: new Date().toISOString(),
          exported_by: user?.email,
          format_version: "1.0",
          description: "Event presentation data for OBS/Companion integration"
        }
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
        type: 'application/json' 
      });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${presentationData.event.name.replace(/\s+/g, '_')}_presentation_data.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download presentation data:', err);
      setError('Failed to download presentation data');
    } finally {
      setDownloading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const getSubmissionTypeIcon = (type: string): string => {
    switch (type) {
      case 'document': return '📄';
      case 'video': return '🎥';
      case 'photo': return '📷';
      default: return '📎';
    }
  };

  const getSubmissionTypeColor = (type: string): string => {
    switch (type) {
      case 'document': return '#007bff';
      case 'video': return '#dc3545';
      case 'photo': return '#28a745';
      default: return '#6c757d';
    }
  };

  const renderSubmissionCard = (submission: PresentationSubmission) => (
    <div 
      key={submission.submission_id} 
      className="card" 
      style={{ 
        marginBottom: '10px', 
        padding: '15px',
        border: '1px solid #e0e0e0',
        borderRadius: '8px'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontSize: '20px' }}>
              {getSubmissionTypeIcon(submission.submission_type)}
            </span>
            <h4 style={{ margin: 0, color: '#333' }}>{submission.filename}</h4>
            <span 
              style={{
                backgroundColor: getSubmissionTypeColor(submission.submission_type),
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              {submission.submission_type.toUpperCase()}
            </span>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', fontSize: '14px', color: '#666' }}>
            <div><strong>Size:</strong> {formatFileSize(submission.file_size)}</div>
            <div><strong>Uploaded:</strong> {formatDate(submission.uploaded_at)}</div>
            <div><strong>By:</strong> {submission.uploaded_by_email}</div>
            <div><strong>Status:</strong> {submission.processing_status}</div>
          </div>

          {submission.original_dimensions && (
            <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
              <strong>Original:</strong> {submission.original_dimensions.width}x{submission.original_dimensions.height}
              {submission.processed_dimensions && (
                <span style={{ marginLeft: '15px' }}>
                  <strong>Processed:</strong> {submission.processed_dimensions.width}x{submission.processed_dimensions.height}
                </span>
              )}
            </div>
          )}

          {submission.error && (
            <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '5px' }}>
              <strong>Error:</strong> {submission.error}
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {submission.download_url && (
            <a 
              href={submission.download_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn"
              style={{ 
                fontSize: '12px', 
                padding: '5px 10px',
                textDecoration: 'none',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px'
              }}
            >
              📥 Original
            </a>
          )}
          {submission.processed_file?.download_url && (
            <a 
              href={submission.processed_file.download_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn"
              style={{ 
                fontSize: '12px', 
                padding: '5px 10px',
                textDecoration: 'none',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px'
              }}
            >
              🎯 Processed
            </a>
          )}
        </div>
      </div>
    </div>
  );

  const renderTeamCard = (team: PresentationTeam) => (
    <div key={team.team_id} className="card" style={{ marginBottom: '30px' }}>
      <div style={{ 
        padding: '20px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
          <h2 style={{ margin: 0, color: '#333' }}>{team.name}</h2>
          <Link 
            to={`/teams/${team.team_id}`}
            className="btn"
            style={{ fontSize: '14px', padding: '8px 16px' }}
          >
            View Team Details
          </Link>
        </div>
        
        {team.description && (
          <p style={{ color: '#666', marginBottom: '15px', fontSize: '16px' }}>
            {team.description}
          </p>
        )}
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', fontSize: '14px' }}>
          <div>
            <strong>👥 Members:</strong> {team.member_count}
            <div style={{ marginTop: '5px' }}>
              {team.members.slice(0, 3).map((member, index) => (
                <div key={member.email} style={{ fontSize: '12px', color: '#666' }}>
                  {member.display_name} {member.role === 'creator' && '(Creator)'}
                </div>
              ))}
              {team.members.length > 3 && (
                <div style={{ fontSize: '12px', color: '#999' }}>
                  +{team.members.length - 3} more
                </div>
              )}
            </div>
          </div>
          
          <div>
            <strong>📁 Submissions:</strong> {team.submissions.total_count}
            <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
              📄 {team.submissions.by_type.document} documents •{' '}
              🎥 {team.submissions.by_type.video} videos •{' '}
              📷 {team.submissions.by_type.photo} photos
            </div>
          </div>
          
          <div>
            <strong>📅 Created:</strong> {formatDate(team.created_at)}
          </div>
          
          {team.desired_skills.length > 0 && (
            <div>
              <strong>🛠️ Skills:</strong>
              <div style={{ marginTop: '5px' }}>
                {team.desired_skills.slice(0, 3).map((skill, index) => (
                  <span 
                    key={skill}
                    style={{
                      display: 'inline-block',
                      backgroundColor: '#e9ecef',
                      color: '#495057',
                      padding: '2px 6px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      marginRight: '4px',
                      marginBottom: '2px'
                    }}
                  >
                    {skill}
                  </span>
                ))}
                {team.desired_skills.length > 3 && (
                  <span style={{ fontSize: '11px', color: '#666' }}>
                    +{team.desired_skills.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {team.submissions.files.length > 0 ? (
        <div style={{ padding: '20px' }}>
          <h3 style={{ marginBottom: '15px', color: '#333' }}>
            📁 Team Submissions ({team.submissions.total_count})
          </h3>
          {team.submissions.files.map(renderSubmissionCard)}
        </div>
      ) : (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: '#666',
          backgroundColor: '#f8f9fa'
        }}>
          <p>No submissions yet</p>
        </div>
      )}
    </div>
  );

  if (!isOrganizer) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
        <h3>Access Denied</h3>
        <p>Only organizers can access presentation data.</p>
        <Link to="/events" className="btn">Back to Events</Link>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading presentation data...</div>;
  }

  if (error) {
    return (
      <div className="error">
        {error}
        <div style={{ marginTop: '20px' }}>
          <button onClick={loadPresentationData} className="btn" style={{ marginRight: '10px' }}>
            Try Again
          </button>
          <Link to="/events" className="btn btn-secondary">
            Back to Events
          </Link>
        </div>
      </div>
    );
  }

  if (!presentationData) {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        <h3>Presentation data not available</h3>
        <Link to="/events" className="btn">Back to Events</Link>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <Link to={`/events/${eventId}`} className="btn btn-secondary">
          ← Back to Event
        </Link>
      </div>

      <div className="card" style={{ marginBottom: '30px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '20px'
        }}>
          <div>
            <h1 style={{ margin: 0, marginBottom: '10px' }}>
              🎥 Event Presentation View
            </h1>
            <h2 style={{ margin: 0, color: '#666' }}>
              {presentationData.event.name}
            </h2>
          </div>
          
          <button
            onClick={downloadPresentationJSON}
            disabled={downloading}
            className="btn"
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            {downloading ? '⏳ Downloading...' : '📥 Download JSON for OBS/Companion'}
          </button>
        </div>

        <p style={{ color: '#666', marginBottom: '20px', fontSize: '16px' }}>
          {presentationData.event.description}
        </p>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '20px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <div>
            <h3 style={{ margin: 0, color: '#007bff' }}>📊 Summary</h3>
            <p style={{ margin: '5px 0', fontSize: '14px' }}>
              <strong>{presentationData.total_teams}</strong> teams<br/>
              <strong>{presentationData.total_submissions}</strong> total submissions
            </p>
          </div>
          
          <div>
            <h3 style={{ margin: 0, color: '#007bff' }}>📅 Deadlines</h3>
            <p style={{ margin: '5px 0', fontSize: '14px' }}>
              <strong>Registration:</strong> {formatDate(presentationData.event.registration_deadline)}<br/>
              <strong>Overview:</strong> {formatDate(presentationData.event.submission_deadline_overview)}<br/>
              <strong>Media:</strong> {formatDate(presentationData.event.submission_deadline_media)}
            </p>
          </div>
          
          <div>
            <h3 style={{ margin: 0, color: '#007bff' }}>🕒 Generated</h3>
            <p style={{ margin: '5px 0', fontSize: '14px' }}>
              {formatDate(presentationData.generated_at)}
            </p>
          </div>

          <div>
            <h3 style={{ margin: 0, color: '#007bff' }}>ℹ️ Usage</h3>
            <p style={{ margin: '5px 0', fontSize: '14px' }}>
              Download JSON for OBS scene setup<br/>
              Presigned URLs valid for 24 hours
            </p>
          </div>
        </div>
      </div>

      {presentationData.teams.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <h4>No teams have registered yet</h4>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Teams will appear here once they register for this event.
          </p>
        </div>
      ) : (
        <div>
          {/* Teams Summary Table */}
          <div className="card" style={{ marginBottom: '30px' }}>
            <h2 style={{ marginBottom: '20px' }}>📋 Teams Overview</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ 
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'left',
                      fontWeight: 'bold',
                      color: '#495057'
                    }}>
                      Team
                    </th>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: '#495057',
                      width: '120px'
                    }}>
                      Planning Sheet
                    </th>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: '#495057',
                      width: '120px'
                    }}>
                      Team Photo
                    </th>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: '#495057',
                      width: '120px'
                    }}>
                      Presentation
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {presentationData.teams.map((team, index) => {
                    const hasPlanningSheet = team.submissions.files.some(f => 
                      f.submission_type === 'document'
                    );
                    const hasTeamPhoto = team.submissions.files.some(f => 
                      f.submission_type === 'photo'
                    );
                    const hasPresentation = team.submissions.files.some(f => 
                      f.submission_type === 'video'
                    );

                    return (
                      <tr key={team.team_id} style={{ 
                        borderBottom: '1px solid #dee2e6',
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa'
                      }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: 'bold', color: '#495057' }}>
                            {team.name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                            {team.submissions.total_count} file{team.submissions.total_count !== 1 ? 's' : ''}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          {hasPlanningSheet ? (
                            <span style={{ color: '#28a745', fontSize: '18px' }}>✅</span>
                          ) : (
                            <span style={{ color: '#dc3545', fontSize: '18px' }}>❌</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          {hasTeamPhoto ? (
                            <span style={{ color: '#28a745', fontSize: '18px' }}>✅</span>
                          ) : (
                            <span style={{ color: '#dc3545', fontSize: '18px' }}>❌</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          {hasPresentation ? (
                            <span style={{ color: '#28a745', fontSize: '18px' }}>✅</span>
                          ) : (
                            <span style={{ color: '#dc3545', fontSize: '18px' }}>❌</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detailed Teams Section */}
          <h2 style={{ marginBottom: '20px' }}>
            👥 Teams and Submissions ({presentationData.total_teams})
          </h2>
          {presentationData.teams.map(renderTeamCard)}
        </div>
      )}
    </div>
  );
};

export default EventPresentationView;
